import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks';
import { sessionStorage, STORAGE_KEYS } from '../utils/sessionStorage';
import AuthGuard from '../components/AuthGuard';
import AdminGuard from '../components/AdminGuard';
import MainLayout from '../layouts/MainLayout';
import LoginScreen from '../screens/LoginScreen';
import HomeScreen from '../screens/HomeScreen';
import PaySomeoneScreen from '../screens/PaySomeoneScreen';
import PackageTrackingScreen from '../screens/PackageTrackingScreen';
import NotificationsScreen from '../screens/NotificationsScreen';
import PaymentSummaryScreen from '../screens/PaymentSummaryScreen';
import TrackingStatusScreen from '../screens/TrackingStatusScreen';
import TransactionsScreen from '../screens/TransactionsScreen';
import PaymentProcessingScreen from '../screens/PaymentProcessingScreen';
import ReleaseFundsScreen from '../screens/ReleaseFundsScreen';
import AuthCallbackScreen from '../screens/AuthCallbackScreen';
import LogoutScreen from '../screens/LogoutScreen';
import AdminScreen from '../screens/admin/AdminScreen';
import AdminTransactionsScreen from '../screens/admin/AdminTransactionsScreen';
// import AdminNotificationTestingScreen from '../screens/admin/AdminNotificationTestingScreen';
import AdminDisputesScreen from '../screens/admin/AdminDisputesScreen';
import DisputeDetail from '../components/DisputeDetail/DisputeDetail';
import ProfileScreen from '../screens/ProfileScreen';
import ProfileNotificationSettingsScreen from '../screens/ProfileNotificationSettingsScreen';
import WithdrawalScreen from '../screens/WithdrawalScreen';
import OnboardingScreen from '../screens/OnboardingScreen';

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Route information interface
  interface RouteInfo {
    pathname: string;
    search?: string;
    hash?: string;
    state?: any;
  }
  
  // Handle navigation based on authentication state
  useEffect(() => {
    console.log('AppRoutes: Authentication state changed or location changed');
    console.log('AppRoutes: isAuthenticated =', isAuthenticated, 'pathname =', location.pathname);
    
    // Skip redirect logic for callback and logout routes
    if (location.pathname === '/callback' || location.pathname === '/logout') {
      return;
    }
    
    if (isAuthenticated && location.pathname === '/login') {
      console.log('AppRoutes: User is authenticated but on login page, redirecting');
      
      // Check if we have a saved route to restore
      const savedRoute = sessionStorage.getItem<RouteInfo>(STORAGE_KEYS.ROUTE);
      console.log('AppRoutes: Saved route =', savedRoute?.pathname || 'none');
      
      if (savedRoute) {
        // Navigate to the saved route
        console.log('AppRoutes: Navigating to saved route:', savedRoute.pathname);
        navigate(savedRoute.pathname, {
          replace: true,
          state: savedRoute.state
        });
      } else {
        // No saved route, go to default route
        console.log('AppRoutes: No saved route, navigating to home');
        navigate('/home', { replace: true });
      }
    }
    
    // We've removed the automatic redirection to login for unauthenticated users
    // This is now handled by the AuthGuard component
  }, [isAuthenticated, location.pathname, navigate]);
  
  // Clear saved route after successful navigation away from login
  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/login' && 
        location.pathname !== '/callback' && location.pathname !== '/logout') {
      const savedRoute = sessionStorage.getItem<RouteInfo>(STORAGE_KEYS.ROUTE);
      
      // If we've successfully navigated to the saved route, we can clear it
      if (savedRoute && location.pathname === savedRoute.pathname) {
        console.log('AppRoutes: Successfully navigated to saved route, clearing it');
        // Only clear the route if we've successfully navigated to it
        // This prevents losing the route on page refresh
        sessionStorage.removeItem(STORAGE_KEYS.ROUTE);
      }
    }
  }, [isAuthenticated, location.pathname]);
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/callback" element={<AuthCallbackScreen />} />
      <Route path="/logout" element={<LogoutScreen />} />
      
      {/* Temporary routes for testing - bypasses authentication */}
      {/* <Route path="/test-price-tag" element={<PriceTagDemoScreen />} /> */}
      
      {/* Protected routes */}
      <Route element={<AuthGuard />}>
        <Route element={<MainLayout />}>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<HomeScreen />} />
          <Route path="/pay" element={<PaySomeoneScreen />} />
          <Route path="/tracking/add/:id?" element={<PackageTrackingScreen />} />
          <Route path="/notifications" element={<NotificationsScreen />} />
          <Route path="/payment/:id" element={<PaymentSummaryScreen />} />
          <Route path="/tracking/:id" element={<TrackingStatusScreen />} />
          <Route path="/transactions" element={<TransactionsScreen />} />
          <Route path="/disputes/:id" element={<DisputeDetail />} />
          <Route path="/payment-processing/:reference" element={<PaymentProcessingScreen />} />
          <Route path="/release-funds/:id" element={<ReleaseFundsScreen />} />
          <Route path="/withdraw" element={<WithdrawalScreen />} />
          <Route path="/profile" element={<ProfileScreen />} />
          <Route path="/profile/notifications" element={<ProfileNotificationSettingsScreen />} />
          <Route path="/onboarding" element={<OnboardingScreen />} />
          
          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminScreen />
              </AdminGuard>
            }
          >
            <Route path="transactions" element={<AdminTransactionsScreen />} />
            <Route path="disputes" element={<AdminDisputesScreen />} />
            <Route path="disputes/:id" element={<DisputeDetail />} />
            {/* <Route path="notifications" element={<AdminNotificationTestingScreen />} /> */}
          </Route>
        </Route>
      </Route>
      
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;